// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import CheckCircle from '@mui/icons-material/CheckCircle'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import moment from 'moment'
import React, { useState } from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { removeTypename } from '../../../common/utils'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import ImageOverlay from '../ImageOverlay/ImageOverlay'
import RequiresAction from '../RequiresAction'
import WeeklyInfoPage from '../WeeklyInfoPage'
import Images from '../components/Images'
import Links from '../components/Links'
import { fetchKitchenPageScoreCardStats, fetchWeeklyInfo, fetchWeeklyInfoScoreCard } from '../weeklyInfoQueries'
import type {
  BuffetWastage as BuffetWastageType,
  WeeklyInfoBuffetWastage,
  WeeklyInfoKitchen,
  WeeklyInfoVariables,
} from '../weeklyInfoTypes'
import css from './kitchenPage.css'

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

type BuffetWastageColumnProps = {
  data: BuffetWastageType
}

const BuffetWastageColumn = ({ data }: BuffetWastageColumnProps) => (
  <TableCell className={cx(css.tableCell, css.checkboxCell)}>
    <CheckCircle className={data.isAvailable ? css.checkIconChecked : css.checkIcon} />
  </TableCell>
)

type BuffetWastageProps = {
  stationId: string
  date: string
  data: WeeklyInfoBuffetWastage
  canEdit: boolean
}

const BuffetWastage = ({ stationId, date }: BuffetWastageProps) => (
  <Query<
    {
      weeklyInfo: Array<WeeklyInfoBuffetWastage>
    },
    WeeklyInfoVariables
  >
    query={fetchWeeklyInfo}
    variables={{
      stationId,
      date,
      type: 'BuffetWastage',
    }}
  >
    {({ data, error, loading }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        {get(data, 'weeklyInfo[0].buffetWastages', []).length > 0 && (
          <div className={css.salesIncentive}>
            <Typography variant='h6' gutterBottom>
              {i18n.t('weeklyInfo.title.buffetWastage', {
                week: `vko. ${moment(date).isoWeek()} ${moment(date).startOf('week').format('D.M.')}-${moment(date)
                  .endOf('week')
                  .format('D.M.')}`,
              })}
            </Typography>
            <RestrictedConsumer>
              {(hasPermission) => (
                <Paper>
                  <Table className={css.table} size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        {weekdays.map((weekday) => (
                          <TableCell className={cx(css.tableHeading, css.checkboxCell)} key={weekday}>
                            {i18n.t(`weeklyInfo.heading.weekday${weekday}`)}
                          </TableCell>
                        ))}
                        <TableCell className={cx(css.tableHeading, css.actionCell)}>
                          {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className={css.tableHeading}>{i18n.t('weeklyInfo.heading.checked')}</TableCell>
                        {get(data, 'weeklyInfo[0].buffetWastages', []).map((item) => (
                          <BuffetWastageColumn data={item} key={item.date} />
                        ))}
                        <TableCell className={cx(css.tableCell, css.actionCell)}>
                          {hasPermission && (
                            <RequiresAction
                              stationId={stationId}
                              date={date}
                              weeklyInfoId={data.weeklyInfo[0].id}
                              subject='Kitchen'
                              isActionRequired={data.weeklyInfo[0].isActionRequired}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </RestrictedConsumer>
          </div>
        )}
      </Loader>
    )}
  </Query>
)

type ProductGroupWastageRowProps = {
  stationId: string
  date: string
  data: WeeklyInfoKitchen
  canEdit: boolean
  wastageBoundaries:
    | {
        lowerWastageBounds: number
        upperWastageBounds: number
      }
    | null
    | undefined
  refetch: (...args: Array<any>) => any
}

const ProductGroupWastageRow = ({
  stationId,
  date,
  data,
  canEdit,
  wastageBoundaries,
  refetch,
}: ProductGroupWastageRowProps) => {
  const [expanded, setExpanded] = useState(false)

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const level3ProductGroup = data.filter((item) => item.productGroupId.length === 7)[0]
  const level4ProductGroups = data
    .filter((item) => item.productGroupId.length > 7)
    .sort((a, b) =>
      (i18n.t(`weeklyInfo.heading.productGroup${a.productGroupName}`) as string).localeCompare(
        i18n.t(`weeklyInfo.heading.productGroup${b.productGroupName}`),
      ),
    )

  const { id, productGroupName, totalWastageAmount, totalWastageVolume, totalSalesAmount, totalSalesVolume } =
    level3ProductGroup
  const percentage = (level3ProductGroup.totalWastageAmount / level3ProductGroup.totalSalesAmount) * 100 || 0
  const totalWastagePercentage = totalSalesAmount > 0 && percentage
  const { lowerWastageBounds, upperWastageBounds } = wastageBoundaries || {}

  const getWastageClass = (value: number) => {
    if (typeof lowerWastageBounds !== 'number' || typeof upperWastageBounds !== 'number') {
      return
    }
    if (value < lowerWastageBounds) return css.below
    if (value > upperWastageBounds) return css.above
    return css.within
  }

  const level3ProductGroupRow = (
    <TableRow className={cx(css.headingRow)} key={id}>
      <TableCell className={cx(css.tableCell, css.arrowCell)}>
        <a className={cx(css.arrow, { [css.open]: expanded })} onClick={toggleExpanded}>
          <KeyboardArrowRightIcon />
        </a>
      </TableCell>
      <TableCell className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`weeklyInfo.heading.productGroup${productGroupName}`)}
      </TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>{Math.round(totalSalesVolume || 0)} kpl</TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>{Math.round(totalWastageVolume || 0)} kpl</TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>{Math.round(totalWastageAmount || 0)} &euro;</TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>
        <span className={cx(getWastageClass(percentage), css.headingCell)}>
          {totalWastagePercentage ? totalWastagePercentage.toFixed(2) + ' %' : '0 %'}
        </span>
        {typeof lowerWastageBounds === 'number' && typeof upperWastageBounds === 'number' && (
          <span>{` (${lowerWastageBounds} - ${upperWastageBounds} %)`}</span>
        )}
      </TableCell>
      <TableCell className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            weeklyInfoId={level3ProductGroup.id}
            subject='Kitchen'
            isActionRequired={level3ProductGroup.isActionRequired}
            refetch={refetch}
          />
        )}
      </TableCell>
    </TableRow>
  )

  const rows = expanded
    ? [
        level3ProductGroupRow,
        ...level4ProductGroups.map((item) => {
          const itemPercentage = (item.totalWastageAmount / item.totalSalesAmount) * 100 || 0
          const itemTotalWastagePercentage = item.totalSalesAmount > 0 && itemPercentage
          return (
            <TableRow key={item.id}>
              <TableCell className={cx(css.tableCell, css.arrowCell)} />
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {i18n.t(`weeklyInfo.heading.productGroup${item.productGroupName}`)}
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {Math.round(item.totalSalesVolume || 0)} kpl
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {Math.round(item.totalWastageVolume || 0)} kpl
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {Math.round(item.totalWastageAmount || 0)} &euro;
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                <span className={getWastageClass(itemPercentage)}>
                  {itemTotalWastagePercentage ? itemTotalWastagePercentage.toFixed(2) + ' %' : '0 %'}
                </span>
              </TableCell>
              <TableCell className={cx(css.tableCell, css.actionCell)}>
                {canEdit && (
                  <RequiresAction
                    stationId={stationId}
                    date={date}
                    weeklyInfoId={item.id}
                    subject='Kitchen'
                    isActionRequired={item.isActionRequired}
                    showCreateTaskButton={false}
                    refetch={refetch}
                  />
                )}
              </TableCell>
            </TableRow>
          )
        }),
      ]
    : level3ProductGroupRow

  return rows
}

type ProductGroupWastageProps = {
  stationId: string
  date: string
}

const ProductGroupWastage = ({ stationId, date }: ProductGroupWastageProps) => (
  <Query<
    {
      weeklyInfo: Array<WeeklyInfoKitchen>
    },
    WeeklyInfoVariables
  >
    query={fetchWeeklyInfo}
    variables={{
      stationId,
      date,
      type: 'Kitchen',
    }}
  >
    {({ data, error, loading, refetch }) => {
      const productGroups = get(data, 'weeklyInfo', [])
      const groupedProductGroups = productGroups.reduce((res, item) => {
        const level3ProductGroupId = item.productGroupId.substring(0, 7)
        if (res[level3ProductGroupId]) {
          res[level3ProductGroupId] = [...res[level3ProductGroupId], item]
        } else {
          res[level3ProductGroupId] = [item]
        }
        return res
      }, {})
      const allWastageBoundaries = get(data, 'wastageBoundaries', [])
      return (
        <Loader
          {...{
            error,
            loading,
          }}
        >
          <div className={css.salesIncentive}>
            <Typography variant='h6' gutterBottom>
              {i18n.t('weeklyInfo.title.productGroupWastage', {
                week: `vko. ${moment(date).isoWeek()} ${moment(date).startOf('week').format('D.M.')}-${moment(date)
                  .endOf('week')
                  .format('D.M.')}`,
              })}
            </Typography>
            <RestrictedConsumer>
              {(hasPermission) => (
                <Paper>
                  <Table className={css.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={cx(css.tableHeading, css.arrowCell)} />
                        <TableCell className={css.tableHeading}>
                          {i18n.t('weeklyInfo.heading.productGroupName')}
                        </TableCell>

                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('weeklyInfo.heading.actualProductsSold')}
                        </TableCell>

                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('weeklyInfo.heading.wastageAmount')}
                        </TableCell>
                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('weeklyInfo.heading.wastageEuros')}
                        </TableCell>
                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('weeklyInfo.heading.wastagePercentage')}
                        </TableCell>

                        <TableCell className={cx(css.tableHeading, css.actionCell)}>
                          {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.values(groupedProductGroups)
                        .filter((productGroups) =>
                          productGroups.some((productGroup) => productGroup.productGroupId.length === 7),
                        )
                        .map((productGroups) => {
                          const level3ProductGroup = productGroups.filter(
                            (productGroup) => productGroup.productGroupId.length === 7,
                          )[0]
                          const wastageBoundaries = allWastageBoundaries.find(
                            (boundary) => boundary.productType === level3ProductGroup.productGroupName.toLowerCase(),
                          )
                          return (
                            <ProductGroupWastageRow
                              stationId={stationId}
                              date={date}
                              data={productGroups}
                              wastageBoundaries={wastageBoundaries}
                              canEdit={hasPermission}
                              key={level3ProductGroup.id}
                              refetch={refetch}
                            />
                          )
                        })}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </RestrictedConsumer>
          </div>
        </Loader>
      )
    }}
  </Query>
)

const ScoreCardTable = ({ stationId, date, currentWeek }) => {
  const getColorClassName = ([category, values]: Row, type: string): string | null | undefined => {
    const { actual, high, low } = values[type]
    let className

    if (!actual && actual !== 0) {
      return null
    }

    className = css.colorCircleYellow

    if (actual >= high) {
      className = css.colorCircleGreen
    } else if (actual <= low) {
      className = css.colorCircleRed
    }

    return className
  }

  return (
    <Query
      query={fetchKitchenPageScoreCardStats}
      variables={{
        stationId,
        date: currentWeek,
      }}
    >
      {({ loading, error, data }) => {
        if (loading || error) {
          return <CircularProgress />
        }

        return (
          <Paper className={css.scoreCard}>
            <Table className={css.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderName')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderRollingMonth')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderLastWeek')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderThisWeek')}</TableCell>
                  <TableCell className={cx(css.tableHeading, css.actionCell)}>
                    {i18n.t('weeklyInfo.heading.isActionRequired')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(removeTypename(data.scoreCardStats)).map((row: any, i: number) => {
                  const [category, values] = row
                  const { thisWeek, lastWeek, rollingMonth } = values || {}

                  return (
                    <TableRow key={i}>
                      <TableCell className={css.tableCell}>{i18n.t(`scoreCard.${category}`)}</TableCell>
                      <TableCell className={css.tableCell}>
                        <div className={css.cellValue}>
                          <span className={cx(css.colorCircle, getColorClassName(row, 'rollingMonth'))} />
                          {rollingMonth.actual}
                        </div>
                      </TableCell>
                      <TableCell className={css.tableCell}>
                        <div className={css.cellValue}>
                          <span className={cx(css.colorCircle, getColorClassName(row, 'lastWeek'))} />
                          {lastWeek.actual}
                        </div>
                      </TableCell>
                      <TableCell className={css.tableCell}>
                        {values.thisWeek && (
                          <div className={css.cellValue}>
                            <span className={cx(css.colorCircle, getColorClassName(row, 'thisWeek'))} />
                            {thisWeek.actual}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={css.actionCell}>
                        <Query
                          query={fetchWeeklyInfoScoreCard}
                          variables={{
                            stationId,
                            date,
                            type: 'ScoreCard',
                            row: `kitchen_${category}`,
                          }}
                        >
                          {({ loading, error, data }) => {
                            if (loading || error) {
                              return <CircularProgress />
                            }

                            const info = get(data, 'weeklyInfo.0', {})
                            return (
                              <RequiresAction
                                stationId={stationId}
                                date={date}
                                subject='Kitchen'
                                isActionRequired={info.isActionRequired}
                                weeklyInfoId={info.id}
                              />
                            )
                          }}
                        </Query>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Paper>
        )
      }}
    </Query>
  )
}

@withUser
class RestaurantPage extends React.Component {
  constructor() {
    super()
    this.state = {
      imageOverlayIsOpen: false,
      selectedImage: null,
    }
  }

  selectImage = (imageContent: string) => {
    this.setState({
      selectedImage: imageContent,
      imageOverlayIsOpen: true,
    })
  }

  render() {
    const { user } = this.props
    const { imageOverlayIsOpen, selectedImage } = this.state
    return (
      <WeeklyInfoPage className={css.component} subject='Kitchen'>
        {({ stationId, date, currentWeek, dateFormat, onDateChange, companyId }) => (
          <React.Fragment>
            <ImageOverlay
              isOpen={imageOverlayIsOpen}
              image={selectedImage}
              onClose={() =>
                this.setState({
                  selectedImage: null,
                  imageOverlayIsOpen: false,
                })
              }
            />

            <div className={css.productGroupWastage}>
              <ProductGroupWastage stationId={stationId} date={date} />
            </div>
            <div className={css.buffetWastage}>
              <BuffetWastage stationId={stationId} date={date} />
            </div>
            <div className={css.scorecardTable}>
              <ScoreCardTable stationId={stationId} date={date} currentWeek={currentWeek} />
            </div>

            <Images stationId={stationId} selectImage={this.selectImage} user={user} imageType={'weekinfo_kitchen'} />

            <Links stationId={stationId} linkType={'weekinfo_kitchen'} companyId={companyId} />
          </React.Fragment>
        )}
      </WeeklyInfoPage>
    )
  }
}

export default RestaurantPage
